import React, { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useT, useLocale } from '../i18n/LocaleContext';
import { useData } from '../data/DataContext';
import { useToast } from '../contexts/ToastContext';
import { useAuth } from '../contexts/AuthContext';
import { useApi } from '../api/client';
import { setEventBooking } from '../api/events';
import MonthlyPilgrimageCard from '../components/events/MonthlyPilgrimageCard';
import Card from '../components/ui/Card';
import Button from '../components/ui/Button';
import Badge from '../components/ui/Badge';
import DataTable from '../components/ui/DataTable';
import EmptyState from '../components/ui/EmptyState';
import SearchRow, { SearchInput } from '../components/ui/SearchRow';
import Select from '../components/ui/Select';
import ConfirmDialog from '../components/ui/ConfirmDialog';
import EventFormModal from '../components/events/EventFormModal';
import EventDetailModal from '../components/events/EventDetailModal';
import { formatCurrency, formatDate } from '../utils/format';
import useDebounce from '../hooks/useDebounce';

const STATUSES = ['open', 'closed', 'completed', 'cancelled'];
const STATUS_TONE = { open: 'open', closed: 'pending', completed: 'completed', cancelled: 'inactive' };

export default function EventsPage() {
  const t = useT();
  const { locale } = useLocale();
  const toast = useToast();
  const { events, createEvent, updateEvent, deleteEvent, refreshAll } = useData();
  const { hasPermission, hasRole } = useAuth();
  const canEditSchedule = hasRole('admin') && hasPermission('settings:manage');
  const canUpdate = hasPermission('events:update');
  const [togglingId, setTogglingId] = useState(null);

  const toggleBooking = async (ev) => {
    const open = !ev.bookingReopened;
    setTogglingId(ev.id);
    try {
      await setEventBooking(ev.id, open);
      toast.success(t(open ? 'events.toast_booking_reopened' : 'events.toast_booking_closed', { name: ev.name }));
      await refreshAll?.();
    } catch (err) {
      toast.error(err?.message || t('toast.generic_error'));
    } finally {
      setTogglingId(null);
    }
  };
  const [params, setParams] = useSearchParams();

  const [query, setQuery] = useState('');
  const [status, setStatus] = useState('');
  const debouncedQuery = useDebounce(query, 200);

  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [viewing, setViewing] = useState(null);
  const [deleting, setDeleting] = useState(null);

  useEffect(() => {
    if (params.get('new') === '1') {
      setEditing(null);
      setFormOpen(true);
      params.delete('new');
      setParams(params, { replace: true });
    }
  }, [params, setParams]);

  const filtered = useMemo(() => {
    const q = debouncedQuery.trim().toLowerCase();
    return events.filter((e) => {
      if (status && e.status !== status) return false;
      if (!q) return true;
      return (
        (e.code || e.id || '').toLowerCase().includes(q) ||
        e.name.toLowerCase().includes(q) ||
        e.destination.toLowerCase().includes(q)
      );
    });
  }, [events, debouncedQuery, status]);

  const statusOptions = useMemo(
    () => [
      { value: '', label: t('events.filter_status') },
      ...STATUSES.map((s) => ({ value: s, label: t(`events.statuses.${s}`) })),
    ],
    [t]
  );

  const openNew = () => { setEditing(null); setFormOpen(true); };
  const openEdit = (ev) => { setEditing(ev); setFormOpen(true); };
  const openView = (ev) => { setViewing(ev); };
  const askDelete = (ev) => { setDeleting(ev); };

  const handleSubmit = async (data) => {
    if (editing) {
      updateEvent(editing.id, data);
      toast.success(t('events.toast_updated', { name: data.name }));
    } else {
      const next = createEvent(data);
      toast.success(t('events.toast_created', { name: next.name }));
    }
    setFormOpen(false);
  };

  const handleDelete = async () => {
    if (!deleting) return;
    deleteEvent(deleting.id);
    toast.success(t('events.toast_deleted'));
    setDeleting(null);
  };

  const columns = [
    { key: 'id', header: t('events.field_reference'), cellClassName: 'mono', render: (r) => r.code || r.id },
    {
      key: 'name',
      header: t('events.field_name'),
      render: (r) => (
        <div>
          <strong>{r.name}</strong>
          {r.autoMonth && <div><Badge tone="pending">{t('events.monthly_badge')}</Badge></div>}
        </div>
      ),
    },
    { key: 'destination', header: t('events.field_destination'), render: (r) => r.destination },
    { key: 'date', header: t('events.field_date'), render: (r) => formatDate(r.date, t) },
    { key: 'capacity', header: t('events.field_capacity'), render: (r) => (r.capacity > 0 ? r.capacity : t('events.no_limit')) },
    {
      key: 'registered',
      header: t('events.field_registered'),
      render: (r) => (r.capacity > 0
        ? t('events.registered_count', { count: r.registered, percent: Math.round((r.registered / r.capacity) * 100) })
        : r.registered),
    },
    { key: 'price', header: t('events.field_price'), render: (r) => formatCurrency(r.price, locale) },
    { key: 'status', header: t('common.status'), render: (r) => <Badge tone={STATUS_TONE[r.status]}>{t(`events.statuses.${r.status}`)}</Badge> },
    ...(useApi ? [{
      key: 'booking',
      header: t('events.field_booking'),
      render: (r) => (r.status !== 'open' ? '—' : (
        <div className="booking-cell">
          <Badge tone={r.bookingOpen ? 'open' : 'inactive'}>
            {r.bookingOpen
              ? (r.bookingReopened ? t('events.booking_reopened') : t('events.booking_open'))
              : t('events.booking_closed')}
          </Badge>
          {r.bookingClosesAt && (
            <div className="booking-cell__when">
              {t(r.bookingReopened ? 'events.booking_until' : 'events.booking_closes', {
                when: new Date(r.bookingClosesAt).toLocaleString(locale === 'fr' ? 'fr-FR' : 'en-GB', {
                  day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit', timeZone: 'Africa/Kigali',
                }),
              })}
            </div>
          )}
          {canUpdate && (!r.bookingOpen || r.bookingReopened) && (
            <button type="button" className="action-btn" disabled={togglingId === r.id} onClick={() => toggleBooking(r)}>
              {r.bookingReopened ? t('events.action_close_booking') : t('events.action_reopen_booking')}
            </button>
          )}
        </div>
      )),
    }] : []),
    {
      key: 'actions',
      header: t('common.actions'),
      render: (r) => (
        <div className="action-row">
          <button type="button" className="action-btn" onClick={() => openView(r)}>{t('common.view')}</button>
          <button type="button" className="action-btn" onClick={() => openEdit(r)}>{t('common.edit')}</button>
          <button type="button" className="action-btn danger" onClick={() => askDelete(r)}>{t('common.delete')}</button>
        </div>
      ),
    },
  ];

  return (
    <div>
      {useApi && <MonthlyPilgrimageCard canEdit={canEditSchedule} />}

      <SearchRow>
        <SearchInput value={query} onChange={setQuery} placeholder={t('events.search_placeholder')} />
        <Select value={status} onChange={(e) => setStatus(e.target.value)} options={statusOptions} className="filter-select" />
        <Button variant="gold" onClick={openNew}>+ {t('events.new_event')}</Button>
      </SearchRow>

      <Card
        title={t('events.title')}
        subtitle={t('events.subtitle')}
        padded={false}
      >
        <DataTable
          columns={columns}
          rows={filtered}
          empty={(
            <EmptyState
              title={t('events.empty_title')}
              body={t('events.empty_body')}
              action={<Button variant="gold" onClick={openNew}>+ {t('events.new_event')}</Button>}
            />
          )}
        />
      </Card>

      <EventFormModal
        isOpen={formOpen}
        onClose={() => setFormOpen(false)}
        onSubmit={handleSubmit}
        initial={editing}
      />

      <EventDetailModal
        isOpen={Boolean(viewing)}
        onClose={() => setViewing(null)}
        event={viewing}
        onEdit={() => { setEditing(viewing); setViewing(null); setFormOpen(true); }}
      />

      <ConfirmDialog
        isOpen={Boolean(deleting)}
        onClose={() => setDeleting(null)}
        onConfirm={handleDelete}
        title={t('events.delete_confirm_title')}
        body={deleting ? t('events.delete_confirm_body', { name: deleting.name }) : ''}
        confirmLabel={t('common.delete')}
      />
    </div>
  );
}
